/**
 * Unified JSON schema form group
 */
import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  QueryList,
  ViewChildren,
  ViewEncapsulation,
  Type,
} from '@angular/core';
import { IFormLayout } from '@app/shared/types';
import {
  assign,
  get,
  gt,
  includes,
  invoke,
  isEqual,
  map,
  reduce,
  some,
} from 'lodash';
import {
  IFieldPreferences,
  IFormGroupPreferences,
} from '../services/schema-form';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-schema-form-group',
  templateUrl: 'schema-form-group.component.html',
  styleUrls: ['schema-form-group.component.scss'],
  encapsulation: ViewEncapsulation.None,
  // Notice: It is not possible to just switch this component to OnPush without breaking a big part of the app
})
export class XVISchemaFormGroupComponent implements OnChanges {
  @Input() public dynamicWidgetsMap: Record<string, Type<any>>;
  @Input() public group: IFormGroupPreferences;
  @Input() public defaultWidgetFamily: string = 'legacy';
  @ViewChildren('groupWidgets') public groupWidgets: QueryList<ElementRef>;

  public visibleFields: {
    isFullWidth: boolean;
    fields: IFieldPreferences[];
  }[] = [];

  public defaultColumnCount: number = 1;

  public groupLayout: IFormLayout;

  public get groupColCount(): number {
    return get(this.groupLayout, 'columnCount', this.defaultColumnCount);
  }

  public get isMultiColumn(): boolean {
    return gt(this.groupColCount, 1);
  }

  public ngOnChanges(): void {
    this.groupLayout = assign(
      {},
      this.groupLayout,
      get(this.group, 'layout', {}),
    );

    const visibleFields = get(
      this.group,
      'fields',
      [] as IFieldPreferences[],
    ) as IFieldPreferences[];

    const fieldsWithDefaults = map(visibleFields, (field) =>
      Object.assign({ widgetFamily: this.defaultWidgetFamily }, field, {
        wrapperClass: this.getComputedStyle(field),
      }),
    );
    this.visibleFields = this.groupVisibleFieldsByFullWidth(fieldsWithDefaults);
  }

  public focus(): void {
    const groupWidgetsArray = this.groupWidgets.toArray();
    const firstWidget = get(groupWidgetsArray, 0, null);
    invoke(firstWidget, 'focus');
  }

  public getComputedStyle(field): string {
    const fieldColumnCount = get(field, 'layout.columnCount');

    const isMultiColumn =
      get(field, 'layout.columnCount', this.isMultiColumn) > 1;

    const styles: string[] = [this.group.itemClass, field.class]
      .map((s) => (typeof s === 'string' ? s.trim() : ''))
      .filter(Boolean);
    if (!styles.length) styles.push('new-line');
    if (!isMultiColumn && !styles.some((style) => style.includes('col-sm-'))) {
      styles.push('col-sm-12');
    }

    // add full-width to those filed which parent support multi-columns, but single by self
    if (
      this.isMultiColumn &&
      isEqual(fieldColumnCount, 1) &&
      !some(styles, (style) => style.includes('full-width'))
    ) {
      styles.push('full-width');
    }

    return styles.join(' ');
  }

  public groupVisibleFieldsByFullWidth(
    fields: IFieldPreferences[],
  ): { isFullWidth: boolean; fields: IFieldPreferences[] }[] {
    const isFullWidth = (field: IFieldPreferences): boolean =>
      includes(field.wrapperClass, 'full-width');

    return reduce(
      fields,
      (acc, field) => {
        const fieldIsFullWidth = isFullWidth(field);
        const lastGroup = acc[acc.length - 1];

        if (!lastGroup || lastGroup.isFullWidth !== fieldIsFullWidth) {
          acc.push({ isFullWidth: fieldIsFullWidth, fields: [field] });
        } else {
          lastGroup.fields.push(field);
        }

        return acc;
      },
      [] as { isFullWidth: boolean; fields: IFieldPreferences[] }[],
    );
  }
}
